import { ReactNode, createContext, useContext } from 'react'

import { useQuery } from '@tanstack/react-query'
import { AnyRouter, RouteApi, getRouteApi, notFound } from '@tanstack/react-router'

import { hrrDetailsQueryOptions } from './query'
import { HRRResponse } from './types'

type HRRContextValue = {
  routeApi: RouteApi<'/_app/hrr/$id', AnyRouter>
  data: HRRResponse
  isLoading: boolean
}

const HRRContext = createContext<HRRContextValue | undefined>(undefined)

export const useHRRContext = () => {
  const context = useContext(HRRContext)
  if (!context) {
    throw new Error('useHRRContext must be used within a HRRContext.Provider')
  }
  return context
}

export const HRRContextProvider = ({ children }: { children: ReactNode }) => {
  const routeApi = getRouteApi('/_app/hrr/$id')
  const search = routeApi.useSearch()
  const { id } = routeApi.useParams()
  const { data, isFetching, isRefetching, isFetched } = useQuery<HRRResponse>(
    hrrDetailsQueryOptions({ ...search, highRiskReviewId: +id })
  )

  if (isFetched && !data) {
    throw notFound()
  }

  if (!data) return null

  return (
    <HRRContext.Provider value={{ routeApi, data, isLoading: isFetching || isRefetching }}>
      {children}
    </HRRContext.Provider>
  )
}
